import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Button,
  Grid,
  Card,
  CardContent,
  useTheme,
  alpha,
  Tabs,
  Tab,
  TextField,
  InputAdornment,
  CircularProgress,
} from '@mui/material';
import { LocationOn, AttachMoney, BusinessCenter, Foundation } from '@mui/icons-material';

import { motion } from 'framer-motion';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import { PropertyService } from '@/services/propertyService';
import { Property, ListingType, Chantier } from '@/types';
import PropertyCard from '@/components/PropertyCard';
import { getLanguage } from '@/utils/i18n';
import PublicHeader from '@/components/PublicHeader';
import PublicFooter from '@/components/PublicFooter';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '@/services/firebase';

// A mock component for Chantier blocks if you don't have ChantierCard yet
const ChantierMockCard: React.FC<{ id: string; title: string; location: string; image: string; }> = ({ id, title, location, image }) => {
  const navigate = useNavigate();
  return (
    <Card 
      onClick={() => navigate(`/chantiers/${id}`)}
      sx={{ 
      m: 1, 
      borderRadius: 4, 
      overflow: 'hidden', 
      position: 'relative',
      height: 300,
      cursor: 'pointer',
      '&:hover img': { transform: 'scale(1.05)' }
    }}>
      <Box
        component="img"
        src={image}
        sx={{ width: '100%', height: '100%', objectFit: 'cover', transition: 'transform 0.5s' }}
      />
      <Box sx={{
        position: 'absolute',
        bottom: 0, left: 0, right: 0,
        background: 'linear-gradient(transparent, rgba(0,0,0,0.8))',
        p: 3,
        color: 'white'
      }}>
        <Typography variant="h6" sx={{ fontWeight: 700, fontFamily: '"Playfair Display", serif' }}>{title}</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, opacity: 0.8 }}>
          <LocationOn fontSize="small" />
          <Typography variant="body2">{location}</Typography>
        </Box>
      </Box>
    </Card>
  );
};

const BuyerLandingPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const language = getLanguage();
  
  const [searchTab, setSearchTab] = useState(0);
  const [properties, setProperties] = useState<Property[]>([]);
  const [featuredChantiers, setFeaturedChantiers] = useState<Chantier[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProps = async () => {
      try {
        const res = await PropertyService.getProperties({ isActive: true, isFeatured: true }, 1, 8);
        if (res.data) {
          setProperties(res.data);
        }
      } catch (e) {
        console.error('Failed to fetch premium properties:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchProps();
  }, []);

  useEffect(() => {
    const q = query(collection(db, 'chantiers'), where('isFeatured', '==', true));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      setFeaturedChantiers(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Chantier)));
    });
    return () => unsubscribe();
  }, []);

  const handleSearch = () => {
    const type = searchTab === 0 ? ListingType.SALE : searchTab === 1 ? ListingType.RENT : ListingType.SALE;
    navigate(`/properties?listingTypes=${type}`);
  };

  const sliderSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 3,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 4000,
    responsive: [
      { breakpoint: 1024, settings: { slidesToShow: 2 } },
      { breakpoint: 600, settings: { slidesToShow: 1 } }
    ]
  };

  return (
    <Box sx={{ flexGrow: 1, bgcolor: 'background.default' }}>
      <PublicHeader 
        onFeaturesClick={() => { window.scrollTo({ top: 800, behavior: 'smooth' }) }}
      />
      
      {/* Search Hero Block */}
      <Box
        sx={{
          py: { xs: 8, md: 12 },
          mt: 8,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.9)} 0%, ${alpha(theme.palette.secondary.main, 0.9)} 100%), url('https://images.unsplash.com/photo-1600596542815-ffad4c1539a9?auto=format&fit=crop&q=80') center/cover`,
          color: 'white',
          position: 'relative',
        }}
      >
        <Container maxWidth="md">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <Typography variant="h2" sx={{ fontWeight: 800, textAlign: 'center', mb: 2, fontFamily: '"Playfair Display", serif', textShadow: '0 4px 12px rgba(0,0,0,0.3)' }}>
              {language === 'en' ? 'Find Your Dream Home' : language === 'fr' ? 'Trouvez la Maison de vos Rêves' : 'ابحث عن منزل أحلامك'}
            </Typography>
            <Typography variant="h6" sx={{ textAlign: 'center', mb: 6, opacity: 0.9, fontWeight: 400 }}>
              {language === 'en' ? 'Search premium properties and exclusive developments.' : language === 'fr' ? 'Recherchez des propriétés premium et des développements exclusifs.' : 'ابحث عن عقارات فاخرة ومشاريع حصرية.'}
            </Typography>

            <Card sx={{ borderRadius: 4, boxShadow: '0 24px 48px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
              <Box sx={{ borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
                <Tabs value={searchTab} onChange={(_, v) => setSearchTab(v)} variant="fullWidth">
                  <Tab label={language === 'en' ? 'Buy' : language === 'fr' ? 'Acheter' : 'شراء'} />
                  <Tab label={language === 'en' ? 'Rent' : language === 'fr' ? 'Louer' : 'إيجار'} />
                  <Tab label={language === 'en' ? 'Sell' : language === 'fr' ? 'Vendre' : 'بيع'} />
                </Tabs>
              </Box>
              <CardContent sx={{ p: 3 }}>
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <TextField 
                      fullWidth 
                      placeholder={language === 'en' ? 'Location (e.g. Casablanca)' : language === 'fr' ? 'Lieu (ex. Casablanca)' : 'الموقع'}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><LocationOn color="primary" /></InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField 
                      fullWidth 
                      placeholder={language === 'en' ? 'Max Price' : language === 'fr' ? 'Prix Max' : 'السعر الأقصى'}
                      InputProps={{
                        startAdornment: <InputAdornment position="start"><AttachMoney color="primary" /></InputAdornment>,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Button 
                      fullWidth 
                      variant="contained" 
                      onClick={handleSearch}
                      size="large"
                      sx={{ py: 1.5, fontSize: '1.1rem', fontWeight: 600 }}
                    >
                      {language === 'en' ? 'Search' : language === 'fr' ? 'Rechercher' : 'بحث'}
                    </Button>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </motion.div>
        </Container>
      </Box>

      {/* Chantiers Carousel */}
      <Box sx={{ py: 10, bgcolor: 'background.paper' }}>
        <Container maxWidth="xl">
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 6, textAlign: 'center', fontFamily: '"Playfair Display", serif' }}>
             {language === 'en' ? 'Exclusive Developments' : language === 'fr' ? 'Développements Exclusifs' : 'مشاريع حصرية'}
          </Typography>
          <Box sx={{ px: { xs: 1, md: 4 } }}>
            <Slider {...sliderSettings}>
              {featuredChantiers.map(chantier => (
                <Box key={chantier.id} sx={{ px: 1 }}>
                  <ChantierMockCard 
                    id={chantier.id} 
                    title={chantier.name} 
                    location={chantier.location?.city || 'Unknown'} 
                    image={chantier.heroVideoUrl || chantier.masterPlanUrl || "https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?auto=format&fit=crop&q=80"} 
                  />
                </Box>
              ))}
            </Slider>
          </Box>
          <Box sx={{ mt: 6, textAlign: 'center' }}>
            <Button 
              variant="outlined" 
              size="large" 
              onClick={() => navigate('/chantiers')}
              sx={{ px: 4, borderRadius: 2 }}
            >
              {language === 'en' ? 'View All Developments' : language === 'fr' ? 'Voir tous les Projets' : 'عرض جميع المشاريع'}
            </Button>
          </Box>
        </Container>
      </Box>

      {/* Featured Units Carousel */}
      <Box sx={{ py: 10, bgcolor: alpha(theme.palette.primary.main, 0.02) }}>
        <Container maxWidth="xl">
          <Typography variant="h3" sx={{ fontWeight: 800, mb: 6, textAlign: 'center', fontFamily: '"Playfair Display", serif' }}>
             {language === 'en' ? 'Featured Properties' : language === 'fr' ? 'Propriétés en Vedette' : 'عقارات مميزة'}
          </Typography>
          
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
              <CircularProgress />
            </Box>
          ) : properties.length > 0 ? (
            <Box sx={{ px: { xs: 1, md: 4 } }}>
              <Slider {...sliderSettings}>
                {properties.map(prop => (
                  <Box key={prop.id} sx={{ px: 1 }}>
                    <PropertyCard property={prop} onView={() => navigate(`/properties/${prop.id}`)} />
                  </Box>
                ))}
              </Slider>
            </Box>
          ) : (
            <Typography sx={{ textAlign: 'center', color: 'text.secondary' }}>No properties found.</Typography>
          )}
        </Container>
      </Box>

      {/* Professional CTA Section */}
      <Box sx={{ py: 10, bgcolor: 'background.paper' }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', borderRadius: 4, display: 'flex', flexDirection: 'column', bgcolor: alpha(theme.palette.primary.main, 0.05), border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <CardContent sx={{ p: 5, textAlign: 'center', flexGrow: 1 }}>
                  <BusinessCenter sx={{ fontSize: 64, color: 'primary.main', mb: 3 }} />
                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, fontFamily: '"Playfair Display", serif' }}>
                    {language === 'en' ? 'Are you an Agency?' : language === 'fr' ? 'Êtes-vous une Agence ?' : 'هل أنت وكالة؟'}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
                    {language === 'en' ? 'List your properties faster, match with clients easily, and close deals effortlessly using our AI CRM.' : language === 'fr' ? 'Listez vos propriétés plus rapidement, trouvez des clients facilement et concluez des affaires sans effort avec notre CRM IA.' : 'اعرض عقاراتك بشكل أسرع، وطابق العملاء بسهولة.'}
                  </Typography>
                  <Button variant="outlined" size="large" onClick={() => navigate('/register/agency')}>
                    {language === 'en' ? 'Join as Agency' : language === 'fr' ? 'Rejoindre en tant qu\'Agence' : 'انضم كوكالة'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', borderRadius: 4, display: 'flex', flexDirection: 'column', bgcolor: alpha(theme.palette.secondary.main, 0.05), border: '1px solid', borderColor: 'divider', boxShadow: 'none' }}>
                <CardContent sx={{ p: 5, textAlign: 'center', flexGrow: 1 }}>
                  <Foundation sx={{ fontSize: 64, color: 'secondary.main', mb: 3 }} />
                  <Typography variant="h4" sx={{ fontWeight: 800, mb: 2, fontFamily: '"Playfair Display", serif' }}>
                    {language === 'en' ? 'Promoteur Immobilier?' : language === 'fr' ? 'Promoteur Immobilier ?' : 'هل أنت مطور عقاري؟'}
                  </Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary', mb: 4 }}>
                    {language === 'en' ? 'Showcase your entire Chantier to thousands of buyers. Manage unit inventory directly from our dedicated tools.' : language === 'fr' ? 'Présentez tout votre Chantier à des milliers d\'acheteurs. Gérez l\'inventaire directement.' : 'اعرض مشروعك لآلاف المشترين. أدر المخزون مباشرة.'}
                  </Typography>
                  <Button variant="contained" color="secondary" size="large" onClick={() => navigate('/register/promoteur')} sx={{ boxShadow: 'none' }}>
                    {language === 'en' ? 'Join as Promoteur' : language === 'fr' ? 'Rejoindre en tant que Promoteur' : 'انضم كمطور عقاري'}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <PublicFooter />
    </Box>
  );
};

export default BuyerLandingPage;
